import type { ReactElement } from 'react';

import type { AutocompleteValue, FieldDef, FormBuilderWidget, FormFieldValue } from '../types';

import { defaultAutocompleteRegistry, type AutocompleteRegistry } from './autocompleteRegistry';
import fieldLabel from './fieldLabel';
import CheckboxField from './fields/CheckboxField';
import DateField from './fields/DateField';
import HtmlContentField from './fields/HtmlContentField';
import ImageField from './fields/ImageField';
import NumberField from './fields/NumberField';
import RadioField from './fields/RadioField';
import SectionHeaderField from './fields/SectionHeaderField';
import SelectField from './fields/SelectField';
import TextareaField from './fields/TextareaField';
import TextField from './fields/TextField';

export interface WidgetRenderProps {
  field: FieldDef;
  value: FormFieldValue;
  onChange: (value: FormFieldValue) => void;
  error?: string;
  disabled?: boolean;
  autocompleteRegistry?: AutocompleteRegistry;
}

type WidgetRenderer = (props: WidgetRenderProps) => ReactElement;

// One place mapping each widget to how it renders — shared by FormRenderer
// (wired to a real form value) and FieldPreview (wired to local state), so
// adding a widget only means touching this file instead of two switches.
const widgetRegistry: Record<FormBuilderWidget, WidgetRenderer> = {
  text: ({ field, value, onChange, error, disabled }) => (
    <TextField
      id={`form-field-${field.key}`}
      label={fieldLabel(field)}
      value={(value as string) ?? ''}
      onChange={onChange}
      error={error}
      disabled={disabled}
      placeholder={field.placeholder}
      description={field.description}
    />
  ),
  textarea: ({ field, value, onChange, error, disabled }) => (
    <TextareaField
      id={`form-field-${field.key}`}
      label={fieldLabel(field)}
      value={(value as string) ?? ''}
      onChange={onChange}
      error={error}
      disabled={disabled}
      placeholder={field.placeholder}
      description={field.description}
    />
  ),
  number: ({ field, value, onChange, error, disabled }) => (
    <NumberField
      id={`form-field-${field.key}`}
      label={fieldLabel(field)}
      value={value as number | string | undefined}
      onChange={onChange}
      error={error}
      disabled={disabled}
      placeholder={field.placeholder}
      description={field.description}
    />
  ),
  date: ({ field, value, onChange, error, disabled }) => (
    <DateField
      id={`form-field-${field.key}`}
      label={fieldLabel(field)}
      value={value as string | null | undefined}
      onChange={onChange}
      error={error}
      disabled={disabled}
      description={field.description}
    />
  ),
  checkbox: ({ field, value, onChange, error, disabled }) => (
    <CheckboxField
      label={fieldLabel(field)}
      value={value as boolean | undefined}
      onChange={onChange}
      error={error}
      disabled={disabled}
      description={field.description}
    />
  ),
  select: ({ field, value, onChange, error, disabled }) => (
    <SelectField
      label={fieldLabel(field)}
      options={field.options ?? []}
      allowOther={field.allowOther}
      multiple={field.multiple}
      value={(value as string | string[] | null) ?? null}
      onChange={onChange}
      error={error}
      disabled={disabled}
      placeholder={field.placeholder}
      description={field.description}
    />
  ),
  radio: ({ field, value, onChange, error, disabled }) => (
    <RadioField
      id={`radio-${field.key}`}
      label={fieldLabel(field)}
      options={field.options ?? []}
      allowOther={field.allowOther}
      value={(value as string | null) ?? null}
      onChange={onChange}
      error={error}
      disabled={disabled}
      description={field.description}
    />
  ),
  autocomplete: ({ field, value, onChange, error, disabled, autocompleteRegistry }) => {
    const Autocomplete = (autocompleteRegistry ?? defaultAutocompleteRegistry)[
      field.model ?? 'staff'
    ];
    return (
      <Autocomplete
        label={fieldLabel(field)}
        multiple={field.multiple}
        value={(value as AutocompleteValue | AutocompleteValue[] | null) ?? null}
        onChange={onChange}
        error={error}
        disabled={disabled}
        placeholder={field.placeholder}
        description={field.description}
      />
    );
  },
  html_content: ({ field }) => <HtmlContentField body={field.body || 'Text to read...'} />,
  image: ({ field }) => <ImageField url={field.url ?? ''} label={field.label || field.key} />,
  section_header: ({ field }) => (
    <SectionHeaderField title={field.label || field.key} description={field.description} />
  )
};

export default widgetRegistry;
